import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  AcademicCapIcon,
  BriefcaseIcon,
  StarIcon,
  TrophyIcon,
  UserGroupIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const TeamPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const mission = {
    title: "Our Mission",
    description: "To democratize institutional-grade liquidity advisory services for the token economy, bridging the gap between traditional finance expertise and blockchain innovation.",
    values: [
      {
        icon: TrophyIcon,
        title: "Excellence",
        description: "We deliver the highest quality analysis and strategic recommendations"
      },
      {
        icon: UserGroupIcon,
        title: "Partnership",
        description: "We work alongside our clients as trusted strategic partners"
      },
      {
        icon: GlobeAltIcon,
        title: "Innovation",
        description: "We continuously evolve our methodologies with the rapidly changing crypto landscape"
      }
    ]
  };

  const founders = [
    {
      name: "Alexander Chen",
      title: "Co-Founder & CEO",
      background: "Former McKinsey & Company Partner",
      experience: "15+ years in strategic consulting and digital transformation",
      education: "MBA Harvard Business School, BS Computer Science MIT",
      expertise: ["Strategic Planning", "Digital Transformation", "Institutional Finance"],
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d"
    },
    {
      name: "Sarah Rodriguez",
      title: "Co-Founder & CTO",
      background: "Former Goldman Sachs VP, Algorithmic Trading",
      experience: "12+ years in quantitative finance and crypto trading",
      education: "PhD Financial Engineering Stanford, MS Mathematics Caltech",
      expertise: ["Quantitative Analysis", "Risk Management", "Blockchain Technology"],
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786"
    }
  ];

  const differentiators = [
    {
      icon: AcademicCapIcon,
      title: "Elite Institutional Background",
      description: "Our team combines decades of experience from top-tier consulting firms, investment banks, and crypto-native organizations.",
      details: ["McKinsey & Company alumni", "Goldman Sachs trading desk veterans", "Early crypto adopters and builders"]
    },
    {
      icon: BriefcaseIcon,
      title: "Proven Track Record",
      description: "We've successfully advised on $2.5B+ in digital assets, helping clients optimize liquidity across 150+ engagements.",
      details: ["150+ successful client engagements", "$2.5B+ assets under analysis", "25+ countries served globally"]
    },
    {
      icon: StarIcon,
      title: "Cutting-Edge Methodology",
      description: "Our proprietary frameworks combine traditional finance principles with blockchain-native analytics and real-time data.",
      details: ["Proprietary scoring algorithms", "Real-time on-chain analytics", "AI-powered optimization models"]
    }
  ];

  return (
    <div className="min-h-screen bg-primary-900 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-primary-800 to-primary-900">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              ref={ref}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-block px-6 py-2 glass-card text-accent-gold text-sm font-medium tracking-wide uppercase mb-6">
                Our Team
              </div>
              <h1 className="text-display font-serif font-bold text-cream-100 mb-6">
                Meet the{' '}
                <span className="text-gradient">Experts Behind Valeon</span>
              </h1>
              <p className="text-body-lg text-cream-200 max-w-3xl mx-auto">
                A world-class team of financial strategists, quantitative analysts, and blockchain experts 
                dedicated to revolutionizing token liquidity advisory.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-primary-900">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="glass-card p-12 mb-16"
            >
              <div className="text-center mb-12">
                <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-6">
                  {mission.title}
                </h2>
                <p className="text-body-lg text-cream-200 max-w-4xl mx-auto leading-relaxed">
                  {mission.description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {mission.values.map((value, index) => (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                    className="text-center group"
                  >
                    <div className="mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-accent-blue to-accent-gold rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                        <value.icon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <h3 className="text-heading-sm font-semibold text-cream-100 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-cream-300 text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-16 bg-gradient-to-b from-primary-900 to-primary-800">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-6">
                Leadership Team
              </h2>
              <p className="text-cream-200 max-w-2xl mx-auto">
                Seasoned executives with deep expertise in traditional finance, 
                strategic consulting, and blockchain technology.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {founders.map((founder, index) => (
                <motion.div
                  key={founder.name}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  className="glass-card p-8 group hover:scale-105 transition-transform duration-300"
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="md:w-1/3">
                      <img 
                        src={founder.image}
                        alt={founder.name}
                        className="w-full aspect-square object-cover rounded-2xl"
                      />
                    </div>
                    <div className="md:w-2/3">
                      <h3 className="text-heading-md font-serif font-bold text-cream-100 mb-2">
                        {founder.name}
                      </h3>
                      <div className="text-accent-blue font-medium mb-1">
                        {founder.title}
                      </div>
                      <div className="text-cream-300 text-sm mb-4">
                        {founder.background}
                      </div>
                      
                      <div className="space-y-3 mb-6">
                        <div>
                          <div className="text-cream-200 text-sm font-medium mb-1">Experience</div>
                          <div className="text-cream-300 text-sm">{founder.experience}</div>
                        </div>
                        <div>
                          <div className="text-cream-200 text-sm font-medium mb-1">Education</div>
                          <div className="text-cream-300 text-sm">{founder.education}</div>
                        </div>
                      </div>

                      <div>
                        <div className="text-cream-200 text-sm font-medium mb-2">Core Expertise</div>
                        <div className="flex flex-wrap gap-2">
                          {founder.expertise.map((skill) => (
                            <span key={skill} className="px-3 py-1 bg-accent-blue/20 text-accent-blue text-xs rounded-full">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators Section */}
      <section className="py-16 bg-primary-800">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-6">
                What Sets Us Apart
              </h2>
              <p className="text-cream-200 max-w-3xl mx-auto">
                Our unique combination of institutional expertise, crypto-native knowledge, 
                and cutting-edge technology delivers unparalleled value to our clients.
              </p>
            </motion.div>

            <div className="space-y-12">
              {differentiators.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  className={`glass-card p-12 ${index % 2 === 1 ? 'ml-auto' : ''} max-w-4xl`}
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                    <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                      <div className="mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-accent-blue to-accent-gold rounded-2xl flex items-center justify-center">
                          <item.icon className="h-8 w-8 text-white" />
                        </div>
                      </div>
                      <h3 className="text-heading-md font-serif font-bold text-cream-100 mb-4">
                        {item.title}
                      </h3>
                      <p className="text-cream-200 mb-6 leading-relaxed">
                        {item.description}
                      </p>
                      <ul className="space-y-2">
                        {item.details.map((detail, idx) => (
                          <li key={idx} className="flex items-center text-sm text-cream-300">
                            <div className="w-1.5 h-1.5 bg-accent-gold rounded-full mr-3" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                      <div className="bg-gradient-to-br from-accent-blue/20 to-accent-gold/20 rounded-2xl p-8 text-center">
                        <div className="text-6xl text-accent-blue mb-4">
                          {index === 0 ? '💼' : index === 1 ? '📈' : '⚡'}
                        </div>
                        <div className="text-cream-100 font-medium">
                          {index === 0 ? 'Elite Background' : index === 1 ? 'Proven Results' : 'Innovation First'}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;